import { useMemo, useState, useCallback } from "react";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments";
import { FileItem } from "./FileItem";
import { useParams } from "react-router-dom";
import { AppButton, ConfirmationModal, LoadingSpinner, Typography } from "@libs/zapi-ui/custom";
import { UploadFilesButton } from "./_UploadFilesButton";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useDocumentsUsefulness } from "./hooks/useDocumentsUsefulness";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { XIcon } from "@phosphor-icons/react";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useContentPreferences } from "./hooks/useContentPreferences";
import { formatCharacterLength } from "@/zapi-frontend/shared/lib/formatCharacterLength";

export function RepositoryIsland({ open }: { open?: boolean }) {
  const { id } = useParams();
  const projectId = useMemo(() => (typeof id === "string" ? id : ""), [id]);

  const { activeChapter } = useChapterStore();
  const chapterId = activeChapter?.id?.toString() || "";

  const { closeHandler } = useProjectSidebarStore();

  const shouldFetchDocuments = !!open && !!projectId;

  const {
    documents,
    isLoading,
    createDocument,
    createWebLink,
    deleteDocument,
    isDocumentsPending,
    isDocumentsProcessing,
    uploadingDocuments,
    setUploadingDocuments,
    retryJuicing,
    summarizeDocument,
    refetchDocuments
  } = useDocuments(projectId, undefined, shouldFetchDocuments ? 3000 : null);

  const { documentsWithUsefulness, isDocumentsUsefulnessLoading, updateUsefulness } = useDocumentsUsefulness(
    projectId,
    chapterId,
    documents,
    shouldFetchDocuments
  );

  const { documentsWithPreferences, isContentPreferencesLoading, updatePreference } = useContentPreferences(
    projectId,
    chapterId,
    documents,
    shouldFetchDocuments
  );

  const [dialogOpenForId, setDialogOpenForId] = useState<number | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<number | null>(null);
  const [isTogglingContentType, setIsTogglingContentType] = useState(false);
  const handleDeleteClick = useCallback((id: number) => {
    setDialogOpenForId(id);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (dialogOpenForId === null) return;
    setIsDeletingId(dialogOpenForId as number);
    deleteDocument(dialogOpenForId as number).finally(() => setIsDeletingId(null));
  }, [dialogOpenForId, deleteDocument]);

  if ((isLoading || isDocumentsUsefulnessLoading || isContentPreferencesLoading) && documents.length === 0) {
    return (
      <div className="flex flex-col w-full h-full border border-innoveraZinc-100 rounded-[8px] bg-innoveraZinc-100">
        <div className="flex flex-col border-b border-innoveraZinc-100 rounded-t-[8px] text-zinc-500 text-sm py-2 px-4 bg-white">
          <div className="flex items-center justify-between">
            <Typography variant="label2" className="text-zinc-900">
              Documents Vault
            </Typography>

            <AppButton appearance="text" size="icon" onClick={closeHandler}>
              <XIcon />
            </AppButton>
          </div>
        </div>
        <div className="h-[calc(100vh-147px)] flex items-center justify-center gap-2" data-testid="repository-loading">
          <LoadingSpinner />
          <Typography variant="body3">Loading documents...</Typography>
        </div>
      </div>
    );
  }

  const documentsWithAllPreferences = documentsWithUsefulness.map((doc) => {
    const prefDoc = documentsWithPreferences.find((p) => p.id === doc.id);
    return {
      ...doc,
      contentType: prefDoc?.contentType || "FULL"
    };
  });

  const totalContentLength = documentsWithAllPreferences.reduce((total, doc) => {
    if (doc.usefulness === "USELESS") return total;
    if (doc.status !== "processed") return total;

    const lengthToUse =
      doc.contentType === "SUMMARIZED" && doc.summaryContentDisplay?.length
        ? doc.summaryContentDisplay?.length || 0
        : doc.fullContentDisplay?.length || 0;

    return total + lengthToUse;
  }, 0);

  if (documentsWithAllPreferences.length > 0) {
    return (
      <div className="flex flex-col w-full h-full border border-innoveraZinc-100 rounded-[8px]">
        <div className="flex flex-col border-b border-innoveraZinc-100 rounded-t-[8px] text-zinc-500 text-sm py-2 px-4">
          <div className="flex items-center justify-between">
            <Typography variant="label2" className="text-zinc-900">
              Documents Vault
            </Typography>

            <AppButton appearance="text" size="icon" onClick={closeHandler}>
              <XIcon />
            </AppButton>
          </div>
        </div>

        <div className={cn("flex w-full h-[calc(100%-90px)] px-2 pb-2 relative")} data-testid="repository-list">
          <div className="relative w-full h-full pr-2 py-4 overflow-y-auto overflow-x-hidden">
            {/* Total Content Length Header */}
            <div className="mb-4 p-3 bg-innoveraZinc-50 rounded-lg border border-innoveraZinc-150">
              <div className="flex items-center justify-between">
                <Typography variant="label2" className="font-medium">
                  Chapter Content Summary
                </Typography>
                <Typography variant="body3" color="zinc500">
                  Total: {formatCharacterLength(totalContentLength)}
                </Typography>
              </div>
              <Typography variant="body3" color="zinc500" className="mt-1">
                {documentsWithAllPreferences.filter((d) => d.usefulness !== "USELESS").length} useful documents •{" "}
                {
                  documentsWithAllPreferences.filter(
                    (d) => d.contentType === "SUMMARIZED" && d.usefulness !== "USELESS"
                  ).length
                }{" "}
                using summaries
              </Typography>
            </div>
            {documentsWithAllPreferences.map((d) => {
              return (
                <FileItem
                  key={d.id}
                  uploadedBy={`${d.createdBy.firstName ?? "Unknown"} ${d.createdBy.lastName ?? ""}`}
                  size={d.fileSize}
                  name={d.fileName}
                  webUrl={d.webUrl}
                  documentId={d.id}
                  deleteDocument={() => handleDeleteClick(d.id)}
                  retryJuicing={async () => retryJuicing(projectId, d.id.toString())}
                  isDeleting={isDeletingId === d.id}
                  status={d.status}
                  usefulness={d.usefulness}
                  onChangeUsefulness={async (usefulness) => {
                    await updateUsefulness(d.id, usefulness);
                  }}
                  refetchDocuments={refetchDocuments}
                  isLoadingDocumentsUsefulness={isDocumentsUsefulnessLoading}
                  contentType={d.contentType}
                  onToggleContentType={async (contentType) => {
                    setIsTogglingContentType(true);
                    await updatePreference(d.id, contentType);
                    setIsTogglingContentType(false);
                  }}
                  onSummarize={async () => await summarizeDocument(d.id)}
                  extractedContent={d.extractedContent}
                  summarizedContent={d.summarizedContent}
                  fullContentDisplay={d.fullContentDisplay}
                  summaryContentDisplay={d.summaryContentDisplay}
                  isTogglingContentType={isTogglingContentType}
                  briefSummary={d.briefSummary?.summary}
                />
              );
            })}
            {uploadingDocuments.length > 0 && (
              <div
                className="flex items-center my-2 text-xs py-1 px-2 bg-blue-100 text-blue-800 rounded max-w-[50%]"
                data-testid="repository-uploading"
              >
                <div className="h-3 w-3 border-2 border-t-transparent border-blue-500 rounded-full animate-spin mr-2 flex-shrink-0"></div>
                <span className="truncate">{`Uploading: ${uploadingDocuments.join(", ")}`}</span>
              </div>
            )}
          </div>

          <ConfirmationModal
            isOpen={dialogOpenForId !== null}
            onClose={() => setDialogOpenForId(null)}
            onConfirm={handleConfirmDelete}
            title="Delete Document"
            description="Are you sure you want to delete this document?"
          />
        </div>
        <UploadFilesButton
          createDocument={(file: File) => createDocument(file, chapterId)}
          createWebLink={(url: string) => createWebLink(url, chapterId)}
          setUploadingDocuments={setUploadingDocuments}
          isDocumentsPending={isDocumentsPending}
          isDocumentsProcessing={isDocumentsProcessing}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full border border-innoveraZinc-100 rounded-[8px] bg-innoveraZinc-100">
      <div className="flex flex-col border-b border-innoveraZinc-100 rounded-t-[8px] text-zinc-500 text-sm py-2 px-4 bg-white">
        <div className="flex items-center justify-between">
          <Typography variant="label2" className="text-zinc-900">
            Documents Vault
          </Typography>

          <AppButton appearance="text" size="icon" onClick={closeHandler}>
            <XIcon />
          </AppButton>
        </div>
      </div>
      <div
        className="flex flex-col justify-center items-center h-full text-center text-gray-500"
        data-testid="repository-empty"
      >
        <Typography variant="body3" className="text-innoveraZinc-500">
          No data in repository
        </Typography>
      </div>
      <UploadFilesButton
        createDocument={(file: File) => createDocument(file, chapterId)}
        setUploadingDocuments={setUploadingDocuments}
        isDocumentsPending={isDocumentsPending}
        isDocumentsProcessing={isDocumentsProcessing}
      />
    </div>
  );
}
